import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { reviews } from '../../mocks/reviews';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

type CategoryFilter = 'all' | 'tech' | 'audio' | 'smart-home' | 'gaming' | 'streaming' | 'health' | 'accessories' | 'trackers' | 'speakers' | 'jewelry' | 'gifts' | 'watches' | 'fashion' | 'beauty' | 'kids' | 'kitchen' | 'spa';

export default function ReviewsPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = 'Product Reviews – Expert Christmas Gift Reviews | Best Christmas Gift Reviews';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Read honest, in-depth Christmas gift reviews from our expert team. Every product is thoroughly tested — tech, jewelry, fashion, beauty, and more.');

    const jsonLd = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          '@id': `${SITE_URL}/reviews#webpage`,
          url: `${SITE_URL}/reviews`,
          name: 'Product Reviews – Expert Christmas Gift Reviews',
          description: 'Read honest, in-depth Christmas gift reviews from our expert team. Every product is thoroughly tested — tech, jewelry, fashion, beauty, and more.',
          isPartOf: { '@id': `${SITE_URL}/#website` },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: 'Reviews', item: `${SITE_URL}/reviews` },
            ],
          },
        },
        {
          '@type': 'ItemList',
          name: 'Christmas Gift Reviews',
          url: `${SITE_URL}/reviews`,
          numberOfItems: reviews.length,
          itemListElement: reviews.slice(0, 10).map((review, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: review.name,
            url: `${SITE_URL}/reviews/${review.id}`,
          })),
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'jsonld-reviews';
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('jsonld-reviews');
      if (el) el.remove();
    };
  }, []);

  const categories: { value: CategoryFilter; label: string; icon: string }[] = [
    { value: 'all', label: 'All Reviews', icon: 'ri-grid-line' },
    { value: 'fragrance', label: 'Fragrance', icon: 'ri-fragrance-line' },
    { value: 'tech', label: 'Tech', icon: 'ri-cpu-line' },
    { value: 'audio', label: 'Audio', icon: 'ri-headphone-line' },
    { value: 'smart-home', label: 'Smart Home', icon: 'ri-home-wifi-line' },
    { value: 'gaming', label: 'Gaming', icon: 'ri-gamepad-line' },
    { value: 'streaming', label: 'Streaming', icon: 'ri-tv-line' },
    { value: 'health', label: 'Health & Fitness', icon: 'ri-heart-pulse-line' },
    { value: 'accessories', label: 'Accessories', icon: 'ri-usb-line' },
    { value: 'trackers', label: 'Trackers', icon: 'ri-map-pin-line' },
    { value: 'speakers', label: 'Smart Speakers', icon: 'ri-speaker-line' },
    { value: 'jewelry', label: 'Jewelry', icon: 'ri-vip-diamond-line' },
    { value: 'gifts', label: 'Gifts', icon: 'ri-gift-line' },
    { value: 'watches', label: 'Watches', icon: 'ri-time-line' },
    { value: 'fashion', label: 'Fashion', icon: 'ri-shirt-line' },
    { value: 'beauty', label: 'Beauty', icon: 'ri-sparkling-line' },
    { value: 'kids', label: 'Kids', icon: 'ri-bear-smile-line' },
    { value: 'kitchen', label: 'Kitchen Appliances', icon: 'ri-restaurant-line' },
    { value: 'spa', label: 'Spa & Wellness', icon: 'ri-heart-pulse-line' },
  ];

  const normalizeCategory = (category: string) => {
    return category.toLowerCase().replace(/\s+/g, '-').replace(/&/g, '').replace(/--/g, '-');
  };

  const filteredReviews = selectedCategory === 'all' 
    ? reviews 
    : reviews.filter(review => normalizeCategory(review.category) === selectedCategory);

  return (
    <div className="min-h-screen bg-[#FFF9F5]">
      <Navbar />
      
      <section className="pt-32 pb-20 bg-gradient-to-b from-[#8B2635] to-[#A52A3A] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 right-20 text-white text-9xl">⭐</div>
          <div className="absolute bottom-20 left-20 text-white text-8xl">🎁</div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center text-white">
            <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6">
              <span className="text-[#D4AF37] font-semibold">⭐ Expert Reviews</span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-bold mb-6">
              Product Reviews
            </h1>
            <p className="text-xl lg:text-2xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
              Honest, detailed reviews from our expert team. Every product is thoroughly tested to help you make confident gift decisions.
            </p>
          </div>
        </div>
      </section>

      <section className="py-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-4">
            <i className="ri-filter-3-line text-[#1B4332]"></i>
            <span className="text-[#1B4332] font-semibold">Filter by Category</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category.value}
                onClick={() => setSelectedCategory(category.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  selectedCategory === category.value
                    ? 'bg-[#1B4332] text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-4">
            Showing <strong className="text-[#1B4332]">{filteredReviews.length}</strong> {filteredReviews.length === 1 ? 'review' : 'reviews'}
            {selectedCategory !== 'all' && (
              <span> in <strong className="text-[#1B4332]">{categories.find(c => c.value === selectedCategory)?.label}</strong></span>
            )}
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredReviews.map((review, index) => (
              <Link
                key={index}
                to={`/reviews/${review.id}`}
                className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group"
              >
                <div className="relative w-full h-64 bg-gray-50 flex items-center justify-center p-8">
                  <img 
                    src={review.image} 
                    alt={review.name}
                    className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 px-3 py-1.5 bg-[#1B4332] rounded-full">
                    <span className="text-white text-xs font-bold">{review.category}</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#1B4332] mb-3 group-hover:text-[#8B2635] transition-colors line-clamp-2">
                    {review.name}
                  </h3>
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className={`ri-star-fill ${i < review.rating ? 'text-[#D4AF37]' : 'text-gray-300'}`}></i>
                    ))}
                    <span className="ml-2 text-gray-600 text-sm">({review.rating})</span>
                  </div>
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-3">
                      <strong className="text-[#1B4332]">Best For:</strong> {review.bestFor}
                    </p>
                    <div className="space-y-1">
                      {review.pros && review.pros[0] && (
                        <p className="text-xs text-green-600 flex items-start gap-1">
                          <i className="ri-check-line mt-0.5 flex-shrink-0"></i>
                          <span className="line-clamp-1">{review.pros[0]}</span>
                        </p>
                      )}
                      {review.pros && review.pros[1] && (
                        <p className="text-xs text-green-600 flex items-start gap-1">
                          <i className="ri-check-line mt-0.5 flex-shrink-0"></i>
                          <span className="line-clamp-1">{review.pros[1]}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-2 text-[#8B2635] font-semibold text-sm group-hover:gap-3 transition-all whitespace-nowrap">
                    Read Full Review
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
